import React from 'react';
import { useAppDispatch, useAppSelector } from '../ui/pages/store';
import { v4 } from 'uuid';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ProductTransactionExportConfiguration } from '../types/product_transaction_export_configuration';
import {
  ProductTransactionExportConfigurationContext,
  ProductTransactionExportConfigurationContextProps,
} from '../context/ProductTransactionExportConfigurationContext';

const productTransactionExportConfigurationState = {
  items: new Map<string, ProductTransactionExportConfiguration>(),
  loading: false,
};

export const productTransactionExportConfigurationSlice = createSlice({
  name: 'productTransactionExportConfiguration',
  initialState: { key: v4() },
  reducers: {
    reset: state => {
      productTransactionExportConfigurationState.items = new Map();
      productTransactionExportConfigurationState.loading = true;
      state.key = v4();
    },
    set: (state, action: PayloadAction<ProductTransactionExportConfiguration[]>) => {
      productTransactionExportConfigurationState.items = new Map();
      action.payload.forEach(p =>
        productTransactionExportConfigurationState.items.set(p.id, new ProductTransactionExportConfiguration(p)),
      );
      state.key = v4();
    },
    add: (state, action: PayloadAction<ProductTransactionExportConfiguration[]>) => {
      action.payload.forEach(p =>
        productTransactionExportConfigurationState.items.set(p.id, new ProductTransactionExportConfiguration(p)),
      );
      productTransactionExportConfigurationState.items = new Map(productTransactionExportConfigurationState.items);
      state.key = v4();
    },
    delete: (state, action: PayloadAction<ProductTransactionExportConfiguration[]>) => {
      action.payload.forEach(p => productTransactionExportConfigurationState.items.delete(p.id));
      productTransactionExportConfigurationState.items = new Map(productTransactionExportConfigurationState.items);
      state.key = v4();
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      productTransactionExportConfigurationState.loading = action.payload;
      state.key = v4();
    },
  },
});

interface ProductTransactionExportConfigurationProviderProps {
  children: React.ReactNode;
}

export default function ProductTransactionExportConfigurationProvider({
  children,
}: ProductTransactionExportConfigurationProviderProps) {
  const dispatch = useAppDispatch();
  const key = useAppSelector(state => state.productTransactionExportConfigurations.key);

  const productTransactionExportConfigurationContext: ProductTransactionExportConfigurationContextProps = {
    productTransactionExportConfigurations: productTransactionExportConfigurationState.items,
    productTransactionExportConfigurationsLoading: productTransactionExportConfigurationState.loading,
    setProductTransactionExportConfigurations: (items: Map<string, ProductTransactionExportConfiguration>) => {
      dispatch(productTransactionExportConfigurationSlice.actions.set([...items.values()]));
    },
  };

  return (
    <ProductTransactionExportConfigurationContext.Provider value={productTransactionExportConfigurationContext}>
      {children}
    </ProductTransactionExportConfigurationContext.Provider>
  );
}
